import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import SectorTradeLifetime from './SectorTradeLifetime';
import StrategyHeader from './StrategyHeader';
import AnalyticsBlock from './AnalyticsBlock';
import { isTodayIST, fmtIST } from './dashboardUtils';
import RecosPanel from './RecosPanel';

const API = 'https://aaktradex.in';

const C = {
  green: '#3B6D11', lgreen: '#EAF3DE', bgreen: '#C0DD97',
  amber: '#854F0B', lamber: '#FAEEDA', bamber: '#FAC775',
  red:   '#A32D2D', lred:   '#FCEBEB', bred:   '#F7C1C1',
  blue:  '#185FA5', lblue:  '#E6F1FB', bblue:  '#B5D4F4',
  purple:'#6B21A8', lpurple:'#F3E8FF', bpurple:'#C4B5FD',
  teal:  '#0D7377', lteal:  '#E0F2F1', bteal:  '#80CBC4',
  gray:  '#6B7280', lgray:  '#F3F4F6', bgray:  '#D1D5DB',
};

function fmtRs(n) {
  if (n == null) return '—';
  const sign = n >= 0 ? '+' : '';
  return `${sign}Rs${Math.round(n).toLocaleString('en-IN')}`;
}
function fmtPct(n) {
  if (n == null) return '—';
  const sign = n >= 0 ? '+' : '';
  return `${sign}${n.toFixed(2)}%`;
}
function fmtLakh(n) {
  if (n == null) return '—';
  return `Rs${(n / 100000).toFixed(2)}L`;
}

// ═══════════════════════════════════════════════════════════
// Sector Momentum Section
// ═══════════════════════════════════════════════════════════
function SectorScores({ data }) {
  if (!data) return null;
  const qualifying = (data.scores || []).filter(s => s.score >= data.qualifying_threshold);
  return (
    <div style={{
      background: 'var(--bg1)', border: '1px solid var(--border2)',
      borderRadius: 10, padding: 14, marginBottom: 12,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.teal }}>
          Sector Momentum ({qualifying.length} qualifying / {data.total_sectors} tracked)
        </div>
        <div style={{ fontSize: 10, color: 'var(--t3)' }}>
          Qualifying threshold: score ≥ {data.qualifying_threshold} · Top {data.max_selected} traded
        </div>
      </div>
      <div style={{ overflow: 'auto' }}>
        <table style={{ fontSize: 11, width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${C.bgray}` }}>
              <th style={{ textAlign: 'left', padding: 4 }}>Sector</th>
              <th style={{ textAlign: 'right', padding: 4 }}>Return</th>
              <th style={{ textAlign: 'right', padding: 4 }}>RS vs Nifty</th>
              <th style={{ textAlign: 'center', padding: 4 }}>Signals</th>
              <th style={{ textAlign: 'center', padding: 4 }}>Score</th>
              <th style={{ textAlign: 'left', padding: 4 }}>Type</th>
            </tr>
          </thead>
          <tbody>
            {(data.scores || []).map(s => {
              const sig = s.signals || {};
              const isQual = s.score >= data.qualifying_threshold;
              return (
                <tr key={s.sector} style={{
                  borderBottom: `0.5px solid ${C.lgray}`,
                  background: isQual ? C.lteal : 'transparent',
                }}>
                  <td style={{ padding: 4, fontWeight: 600 }}>
                    {s.sector.replace('NIFTY_', '')}
                  </td>
                  <td style={{ padding: 4, textAlign: 'right',
                    color: (s.sector_return_pct || 0) >= 0 ? C.green : C.red, fontWeight: 500 }}>
                    {fmtPct(s.sector_return_pct)}
                  </td>
                  <td style={{ padding: 4, textAlign: 'right',
                    color: (s.rs_delta || 0) >= 0 ? C.green : C.red }}>
                    {fmtPct(s.rs_delta)}
                  </td>
                  <td style={{ padding: 4, textAlign: 'center', fontSize: 10 }}>
                    {sig.raw_return_gt_1pct && <span title="Return > 1%">📈</span>}
                    {sig.rs_vs_nifty && <span title="Outperformed Nifty">💪</span>}
                    {sig.catalyst && <span title="Active catalyst">📰</span>}
                  </td>
                  <td style={{ padding: 4, textAlign: 'center', fontSize: 12, fontWeight: 700,
                    color: isQual ? C.teal : 'var(--t3)' }}>
                    {s.score}
                  </td>
                  <td style={{ padding: 4, fontSize: 9, color: 'var(--t3)' }}>
                    {s.type === 'nifty_official' ? 'official' : 'synthetic'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// Catalyst Suggestions Section
// ═══════════════════════════════════════════════════════════
function CatalystSuggestions({ grouped, activeCatalysts, onRefresh }) {
  const [pendingIds, setPendingIds] = useState(new Set());

  const isPending = (id) => pendingIds.has(id);
  const markPending = (id) => setPendingIds(s => { const n = new Set(s); n.add(id); return n; });
  const clearPending = (id) => setPendingIds(s => { const n = new Set(s); n.delete(id); return n; });

  const acceptCatalyst = async (newsId, sectorName, title) => {
    if (isPending(`a${newsId}`)) return;
    markPending(`a${newsId}`);
    try {
      await axios.post(`${API}/sectortrade/catalyst/accept`, {
        news_id: newsId, sector_name: sectorName, catalyst_desc: title, importance: 3,
      });
      onRefresh();
    } catch (e) { console.error(e); alert('Accept failed: ' + (e.response?.data?.detail || e.message)); }
    finally { clearPending(`a${newsId}`); }
  };

  const dismissCatalyst = async (newsId) => {
    if (isPending(`d${newsId}`)) return;
    markPending(`d${newsId}`);
    try {
      await axios.post(`${API}/sectortrade/catalyst/dismiss/${newsId}`);
      onRefresh();
    } catch (e) { console.error(e); alert('Dismiss failed: ' + (e.response?.data?.detail || e.message)); }
    finally { clearPending(`d${newsId}`); }
  };

  const deactivateCatalyst = async (catId) => {
    if (isPending(`x${catId}`)) return;
    markPending(`x${catId}`);
    try {
      await axios.post(`${API}/sectortrade/catalyst/${catId}/deactivate`);
      onRefresh();
    } catch (e) { console.error(e); alert('Deactivate failed: ' + (e.response?.data?.detail || e.message)); }
    finally { clearPending(`x${catId}`); }
  };

  const bySector = grouped?.by_sector || {};
  const totalCount = grouped?.total_suggestions || 0;
  const activeList = activeCatalysts?.catalysts || [];

  return (
    <div style={{
      background: 'var(--bg1)', border: '1px solid var(--border2)',
      borderRadius: 10, padding: 14, marginBottom: 12,
    }}>
      <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'space-between' }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.amber }}>
          📰 Catalyst Suggestions ({totalCount} pending)
        </div>
        <div style={{ fontSize: 10, color: 'var(--t3)' }}>
          Active: {activeList.length} · Last 24h from Moneycontrol
        </div>
      </div>

      {activeList.length > 0 && (
        <div style={{
          background: C.lteal, borderRadius: 6, padding: 8, marginBottom: 10,
          border: `1px solid ${C.bteal}`,
        }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: C.teal, marginBottom: 4 }}>
            ✓ ACTIVE CATALYSTS
          </div>
          {activeList.map(c => (
            <div key={c.id} style={{ fontSize: 11, marginBottom: 4,
              display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <span style={{ fontWeight: 600, color: C.teal }}>
                  {c.sector_name.replace('NIFTY_', '')}
                </span>
                <span style={{ marginLeft: 6, color: 'var(--t2)' }}>
                  {c.catalyst_desc?.slice(0, 90)}
                </span>
              </div>
              <button
                onClick={() => deactivateCatalyst(c.id)}
                disabled={isPending(`x${c.id}`)}
                style={{ fontSize: 9, padding: '2px 6px', border: 'none',
                  background: 'transparent', color: C.red, cursor: 'pointer' }}
                title="Deactivate this catalyst"
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}

      {Object.keys(bySector).length === 0 ? (
        <div style={{ fontSize: 11, color: 'var(--t3)', padding: 8 }}>
          No pending suggestions. Check back after 8 AM news fetch.
        </div>
      ) : (
        Object.entries(bySector).map(([sector, sugs]) => (
          <div key={sector} style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.blue, marginBottom: 4 }}>
              {sector.replace('NIFTY_', '')}
            </div>
            {sugs.map(s => (
              <div key={s.id} style={{
                display: 'flex', alignItems: 'flex-start', gap: 6,
                fontSize: 10, marginBottom: 4, padding: 4,
                background: 'var(--bg2)', borderRadius: 4,
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 500, color: 'var(--t1)' }}>
                    {s.title}
                  </div>
                  <div style={{ fontSize: 9, color: 'var(--t3)', marginTop: 2 }}>
                    score {s.top_score} · kw: {(s.matched_keywords || []).slice(0, 4).join(', ')}
                    {s.url && (
                      <a href={s.url} target="_blank" rel="noopener noreferrer"
                         style={{ marginLeft: 6, color: C.blue, textDecoration: 'none' }}>
                        source →
                      </a>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => acceptCatalyst(s.id, sector, s.title)}
                  disabled={isPending(`a${s.id}`)}
                  style={{
                    fontSize: 9, padding: '3px 8px', border: 'none',
                    background: C.green, color: '#fff', cursor: 'pointer',
                    borderRadius: 3, fontWeight: 600, whiteSpace: 'nowrap',
                  }}
                >
                  ✓ Accept
                </button>
                <button
                  onClick={() => dismissCatalyst(s.id)}
                  disabled={isPending(`d${s.id}`)}
                  style={{
                    fontSize: 9, padding: '3px 8px', border: 'none',
                    background: C.lgray, color: C.gray, cursor: 'pointer',
                    borderRadius: 3, fontWeight: 600, whiteSpace: 'nowrap',
                  }}
                >
                  ✕ Dismiss
                </button>
              </div>
            ))}
          </div>
        ))
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// Watchlist Section (grouped by sector)
// ═══════════════════════════════════════════════════════════
function Watchlist({ data }) {
  if (!data || !data.sectors?.length) {
    return (
      <div style={{
        background: 'var(--bg1)', border: '1px solid var(--border2)',
        borderRadius: 10, padding: 14, marginBottom: 12, fontSize: 11, color: 'var(--t2)',
      }}>
        No watchlist for today. Runs at 8 AM daily.
      </div>
    );
  }
  return (
    <div style={{
      background: 'var(--bg1)', border: '1px solid var(--border2)',
      borderRadius: 10, padding: 14, marginBottom: 12,
    }}>
      <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>
        Watchlist ({data.total_stocks} stocks · {data.sectors.length} sectors · {data.trade_date})
      </div>
      {data.sectors.map(sec => (
        <div key={sec.sector_name} style={{ marginBottom: 8 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.blue, marginBottom: 3 }}>
            {sec.sector_name.replace('NIFTY_', '')} (rank {sec.sector_rank}, score {sec.sector_score})
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {sec.stocks.map(s => (
              <span key={s.ticker} style={{
                fontSize: 10, padding: '3px 8px', borderRadius: 4,
                background: C.lteal, color: C.teal, fontWeight: 500,
                border: `0.5px solid ${C.bteal}`,
              }}>
                {s.ticker}
              </span>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// Live Setup Status
// ═══════════════════════════════════════════════════════════
function LiveStatus({ data }) {
  if (!data || !data.stocks?.length) return null;
  const CONDS = ['timing_ok', 'nifty_ok', 'multi_day_trend', 'green_day',
                 'ema_stack', 'macd_bullish', 'rsi_in_band'];
  return (
    <div style={{
      background: 'var(--bg1)', border: '1px solid var(--border2)',
      borderRadius: 10, padding: 14, marginBottom: 12,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
        <div style={{ fontSize: 13, fontWeight: 700 }}>Live Setup Status</div>
        <div style={{ fontSize: 10, color: 'var(--t3)' }}>
          Nifty {fmtPct(data.nifty_change_pct)} · Open {data.open_positions}/{data.max_positions} · Market {data.market_open ? 'OPEN' : 'CLOSED'}
        </div>
      </div>
      <div style={{ overflow: 'auto' }}>
        <table style={{ fontSize: 10, width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${C.bgray}` }}>
              <th style={{ textAlign: 'left', padding: 4 }}>Ticker</th>
              <th style={{ textAlign: 'left', padding: 4 }}>Sector</th>
              <th style={{ textAlign: 'right', padding: 4 }}>LTP</th>
              <th style={{ textAlign: 'right', padding: 4 }}>Day%</th>
              <th style={{ textAlign: 'center', padding: 4 }}>Conditions (7)</th>
              <th style={{ textAlign: 'center', padding: 4 }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {data.stocks.map(s => (
              <tr key={s.ticker} style={{ borderBottom: `0.5px solid ${C.lgray}` }}>
                <td style={{ padding: 4, fontWeight: 600 }}>{s.ticker}</td>
                <td style={{ padding: 4, fontSize: 9, color: 'var(--t3)' }}>
                  {s.sector_name.replace('NIFTY_', '')}
                </td>
                <td style={{ padding: 4, textAlign: 'right' }}>
                  {s.ltp ? s.ltp.toFixed(2) : '—'}
                </td>
                <td style={{ padding: 4, textAlign: 'right',
                  color: (s.day_change_pct || 0) >= 0 ? C.green : C.red }}>
                  {fmtPct(s.day_change_pct)}
                </td>
                <td style={{ padding: 4, textAlign: 'center' }}>
                  {CONDS.map(k => {
                    const v = s.conditions?.[k];
                    if (v === undefined) return null;
                    return (
                      <span key={k} style={{
                        display: 'inline-block', width: 10, height: 10, borderRadius: 2,
                        margin: '0 1px',
                        background: v ? C.green : C.red,
                      }} title={k}/>
                    );
                  })}
                </td>
                <td style={{ padding: 4, textAlign: 'center' }}>
                  {s.already_in_position ? (
                    <span style={{ fontSize: 9, padding: '2px 6px', borderRadius: 3,
                      background: C.lblue, color: C.blue, fontWeight: 700 }}>
                      IN POS
                    </span>
                  ) : s.all_conditions_met ? (
                    <span style={{ fontSize: 9, padding: '2px 6px', borderRadius: 3,
                      background: C.lgreen, color: C.green, fontWeight: 700 }}>
                      READY
                    </span>
                  ) : (
                    <span style={{ fontSize: 9, color: 'var(--t3)' }}>—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// Positions Section
// ═══════════════════════════════════════════════════════════
function Positions({ data }) {
  if (!data) return null;
  const open = data.open || [];
  const closed = data.closed || [];
  return (
    <div style={{
      background: 'var(--bg1)', border: '1px solid var(--border2)',
      borderRadius: 10, padding: 14, marginBottom: 12,
    }}>
      <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>
        Positions ({open.length} open, {closed.length} closed)
      </div>
      {open.length > 0 && (
        <>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.blue, marginBottom: 4 }}>Open</div>
          <div style={{ overflow: 'auto', marginBottom: 8 }}>
            <table style={{ fontSize: 10, width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${C.bgray}` }}>
                  <th style={{ textAlign: 'left', padding: 4 }}>Ticker</th>
                  <th style={{ textAlign: 'left', padding: 4 }}>Sector</th>
                  <th style={{ textAlign: 'right', padding: 4 }}>Entry</th>
                  <th style={{ textAlign: 'right', padding: 4 }}>LTP</th>
                  <th style={{ textAlign: 'right', padding: 4 }}>Qty</th>
                  <th style={{ textAlign: 'right', padding: 4 }}>SL</th>
                  <th style={{ textAlign: 'right', padding: 4 }}>Unrealized (net)</th>
                  <th style={{ textAlign: 'right', padding: 4 }}>MFE%</th>
                  <th style={{ textAlign: 'center', padding: 4 }}>Stage</th>
                  <th style={{ textAlign: 'left', padding: 4 }}>Entered</th>
                </tr>
              </thead>
              <tbody>
                {open.map(p => (
                  <tr key={p.id} style={{ borderBottom: `0.5px solid ${C.lgray}` }}>
                    <td style={{ padding: 4, fontWeight: 600 }}>{p.ticker}</td>
                    <td style={{ padding: 4, fontSize: 9, color: 'var(--t3)' }}>
                      {p.sector_name?.replace('NIFTY_', '')}
                    </td>
                    <td style={{ padding: 4, textAlign: 'right' }}>{p.entry_price?.toFixed(2)}</td>
                    <td style={{ padding: 4, textAlign: 'right' }}>{p.ltp?.toFixed(2) || '—'}</td>
                    <td style={{ padding: 4, textAlign: 'right' }}>{p.qty}</td>
                    <td style={{ padding: 4, textAlign: 'right', color: C.red }}>
                      {p.stop_loss?.toFixed(2)}
                    </td>
                    <td style={{ padding: 4, textAlign: 'right', fontWeight: 600,
                      color: (p.unrealized_net || 0) >= 0 ? C.green : C.red }}>
                      {fmtRs(p.unrealized_net)} ({fmtPct(p.unrealized_pct)})
                    </td>
                    <td style={{ padding: 4, textAlign: 'right', fontSize: 9, color: 'var(--t3)' }}>
                      {p.mfe_pct ? p.mfe_pct.toFixed(2) + '%' : '—'}
                    </td>
                    <td style={{ padding: 4, textAlign: 'center', fontSize: 9,
                      fontWeight: p.current_stage === 'HALF_BOOKED' ? 700 : 400,
                      color: p.current_stage === 'HALF_BOOKED' ? C.green : 'var(--t3)' }}>
                      {p.current_stage === 'HALF_BOOKED' ? '½ BOOKED' : (p.current_stage || 'INITIAL')}
                    </td>
                    <td style={{ padding: 4, fontSize: 10, color: 'var(--t3)' }}>{fmtIST(p.entry_ts)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      {closed.length > 0 && (() => {
        const todayClosed = closed.filter(p => isTodayIST(p.exit_ts));
        const historyClosed = closed.filter(p => !isTodayIST(p.exit_ts));

        const renderRow = (p) => (
          <tr key={p.id} style={{ borderBottom: `0.5px solid ${C.lgray}` }}>
            <td style={{ padding: 4, fontWeight: 600 }}>{p.ticker}</td>
            <td style={{ padding: 4, fontSize: 9, color: 'var(--t3)' }}>
              {p.sector_name?.replace('NIFTY_', '')}
            </td>
            <td style={{ padding: 4, textAlign: 'right' }}>{p.entry_price?.toFixed(2)}</td>
            <td style={{ padding: 4, textAlign: 'right' }}>{p.exit_price?.toFixed(2)}</td>
            <td style={{ padding: 4, fontSize: 9 }}>{p.exit_reason}</td>
            <td style={{ padding: 4, textAlign: 'right', fontWeight: 600,
              color: (p.net_pnl || 0) >= 0 ? C.green : C.red }}>
              {fmtRs(p.net_pnl)}
            </td>
            <td style={{ padding: 4, fontSize: 10, color: 'var(--t3)' }}>{fmtIST(p.entry_ts)}</td>
            <td style={{ padding: 4, fontSize: 10, color: 'var(--t3)' }}>{fmtIST(p.exit_ts)}</td>
          </tr>
        );

        const renderTable = (rows) => (
          <div style={{ overflow: 'auto', marginBottom: 12 }}>
            <table style={{ fontSize: 10, width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${C.bgray}` }}>
                  <th style={{ textAlign: 'left', padding: 4 }}>Ticker</th>
                  <th style={{ textAlign: 'left', padding: 4 }}>Sector</th>
                  <th style={{ textAlign: 'right', padding: 4 }}>Entry</th>
                  <th style={{ textAlign: 'right', padding: 4 }}>Exit</th>
                  <th style={{ textAlign: 'left', padding: 4 }}>Reason</th>
                  <th style={{ textAlign: 'right', padding: 4 }}>Net P&L</th>
                  <th style={{ textAlign: 'left', padding: 4 }}>Entered</th>
                  <th style={{ textAlign: 'left', padding: 4 }}>Exited</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(renderRow)}
              </tbody>
            </table>
          </div>
        );

        return (
          <>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--t2)', marginBottom: 4, marginTop: 8 }}>
              Closed Today ({todayClosed.length})
            </div>
            {todayClosed.length === 0
              ? <div style={{color: 'var(--t3)', fontSize: 11, marginBottom: 12}}>No positions closed today</div>
              : renderTable(todayClosed)}

            {historyClosed.length > 0 && (
              <details style={{marginTop: 8}}>
                <summary style={{cursor: 'pointer', fontSize: 11, fontWeight: 600, color: '#0050b3', padding: 4}}>
                  ▶ History ({historyClosed.length} older closed) — click to expand
                </summary>
                <div style={{marginTop: 8}}>
                  {renderTable(historyClosed)}
                </div>
              </details>
            )}
          </>
        );
      })()}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// Main tab component
// ═══════════════════════════════════════════════════════════
export default function SectorTradeActive() {
  const [scores, setScores] = useState(null);
  const [watchlist, setWatchlist] = useState(null);
  const [status, setStatus] = useState(null);
  const [positions, setPositions] = useState(null);
  const [summary, setSummary] = useState(null);
  const [suggestions, setSuggestions] = useState(null);
  const [activeCatalysts, setActiveCatalysts] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    try {
      const [sc, wl, st, po, su, sg, ac] = await Promise.all([
        axios.get(`${API}/sectortrade/scores`).catch(() => ({ data: null })),
        axios.get(`${API}/sectortrade/watchlist`).catch(() => ({ data: null })),
        axios.get(`${API}/sectortrade/status`).catch(() => ({ data: null })),
        axios.get(`${API}/sectortrade/positions`).catch(() => ({ data: null })),
        axios.get(`${API}/sectortrade/summary`).catch(() => ({ data: null })),
        axios.get(`${API}/sectortrade/catalyst/suggestions_grouped?per_sector=5`).catch(() => ({ data: null })),
        axios.get(`${API}/sectortrade/catalyst/active`).catch(() => ({ data: null })),
      ]);
      setScores(sc.data); setWatchlist(wl.data); setStatus(st.data);
      setPositions(po.data); setSummary(su.data);
      setSuggestions(sg.data); setActiveCatalysts(ac.data);
    } catch (e) {
      console.error('SectorTrade fetch:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    const t = setInterval(fetchAll, 30000);
    return () => clearInterval(t);
  }, [fetchAll]);

  if (loading) return <div style={{ padding: 16, fontSize: 11 }}>Loading SectorTrade...</div>;

  return (
    <div style={{ padding: 16 }}>
      <StrategyHeader
        strategyKey="sectortrade"
        lifetime={summary}
        openPositions={positions?.open || []}
        todayClosed={(positions?.closed || []).filter(p => isTodayIST(p.exit_ts))}
      />

      <AnalyticsBlock strategyKey="sectortrade" />

      <Positions data={positions} />

      <SectorScores data={scores} />

      <LiveStatus data={status} />

      <details style={{background: '#fff', border: '1px solid #ddd', borderRadius: 8, padding: 12, marginBottom: 12}}>
        <summary style={{cursor: 'pointer', fontWeight: 600, fontSize: 13, color: 'var(--t1)'}}>
          ▶ My Recos — click to expand
        </summary>
        <div style={{marginTop: 12}}>
          <RecosPanel />
        </div>
      </details>

      <CatalystSuggestions
        grouped={suggestions}
        activeCatalysts={activeCatalysts}
        onRefresh={fetchAll}
      />
    </div>
  );
}
